import { useState } from 'react';
import { useParams } from 'react-router-dom';
import List from '../../components/List/List';
import References, { ReferencesToggle } from '../../components/References/References';
import useReferences from '../../components/References/useReferences';
import Spinner from '../../components/Spinner';
import { useCategory } from '../../CategoryContext';
import { categories } from '../../utils/cardInfo';

// Vertical offset that aligns the open References panel with the top of the
// first card. The space above the cards is the page heading plus, for editors
// only, the "Sort by" row — so there are exactly two values (measured against
// the rendered layout). Bump these if the heading/sort row spacing changes.
const REFERENCES_OFFSET_EDITOR = 125;
const REFERENCES_OFFSET_MEMBER = 85;

const PublicationsPage = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [listLoading, setListLoading] = useState(true);
  const { vertical_id } = useParams();
  // Owned here (rather than inside <References>) so the list survives the panel
  // being collapsed/reopened, which unmounts <References>.
  const referencesApi = useReferences(vertical_id);
  const { isEditor } = useCategory();
  const panelTop = isEditor ? REFERENCES_OFFSET_EDITOR : REFERENCES_OFFSET_MEMBER;

  // Show one spinner until both the publications list and the references have
  // loaded, then reveal everything together. The row stays mounted (just hidden)
  // so List and References keep fetching while the spinner is up.
  const pageLoading = listLoading || referencesApi.loading;

  const listProps = {
    category: 'papers',
    heading: 'Publications',
    cardHeader: categories.papers.cardHeader,
    icons: categories.papers.icons,
    dotColor: categories.papers.dotColor,
  };

  // A single, stable tree so <List> keeps the same position across toggles and
  // is never remounted (remounting would refetch the cards). Only the wrapper
  // width, the collapsed toggle, and the open panel change with `isOpen`.
  //
  // Open: the row is constrained so the References panel reaches the right edge
  // of the screen. .showSidebar is centered by `align-items: center` on #root,
  // so its content starts ~300px from the left (not the 500px margin); a width
  // of calc(100vw - 320px) spans from there to ~20px shy of the right edge.
  // Closed: width is unset, so the list reclaims its original full space, and the
  // compact "References Open" strip sits above the first card on its right edge.
  return (
    <>
      {pageLoading && <Spinner />}
      <div
        style={{
          display: pageLoading ? 'none' : 'flex',
          alignItems: 'flex-start',
          gap: '24px',
          width: isOpen ? 'calc(100vw - 320px)' : undefined,
        }}
      >
        <List
          {...listProps}
          width={isOpen ? '100%' : undefined}
          onLoadingChange={setListLoading}
          referencesToggle={
            isOpen ? undefined : <ReferencesToggle onOpen={() => setIsOpen(true)} />
          }
        />
        {isOpen && (
          <References
            onClose={() => setIsOpen(false)}
            style={{ marginTop: panelTop }}
            {...referencesApi}
          />
        )}
      </div>
    </>
  );
};
export default PublicationsPage;
